import React, { useEffect, useRef } from 'react';
import { ArrowRightCircle, Loader2, Wand2 } from 'lucide-react';

/**
 * Fixed-position context menu shown when a reviewer right-clicks a
 * movement segment on the map. Two entries per the OSRM spec
 * (docs/location-tracking/osrm-optimization.md §Endpoints.1):
 *
 *   - `Auto-Correct the path` → GET /match-route?segment_id
 *   - `Auto-Correct + Extend to next segment`
 *                             → GET /match-route?segment_id&extend=true
 *
 * The "Extend" item only appears when the clicked segment has a next
 * segment in the same session (backend requires the pair to share a
 * check-in). Closes on any outside click / escape / scroll so it never
 * lingers over the map.
 */
export interface OsrmSegmentMenuProps {
  x: number;
  y: number;
  segmentId: number;
  extendable: boolean;
  onClose: () => void;
  onCorrect: () => void;
  onExtend: () => void;
  /** When set, disables all entries and shows a spinner — used while a
   *  match-route request is in flight. */
  busy?: boolean;
}

const MENU_WIDTH = 260;
const MENU_HEIGHT_ONE = 78;
const MENU_HEIGHT_TWO = 118;

export const OsrmSegmentMenu: React.FC<OsrmSegmentMenuProps> = ({
  x,
  y,
  segmentId,
  extendable,
  onClose,
  onCorrect,
  onExtend,
  busy = false,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Clamp the menu inside the viewport so it doesn't get cut off at the
  // right/bottom edges of the map. Menu height varies with the number of
  // action rows so we pick the right clamp per state.
  const menuHeight = extendable ? MENU_HEIGHT_TWO : MENU_HEIGHT_ONE;
  const clampedX = typeof window === 'undefined'
    ? x
    : Math.min(x, window.innerWidth - MENU_WIDTH - 8);
  const clampedY = typeof window === 'undefined'
    ? y
    : Math.min(y, window.innerHeight - menuHeight - 8);

  useEffect(() => {
    const handleClickAway = (evt: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(evt.target as Node)) {
        onClose();
      }
    };
    const handleKey = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') onClose();
    };
    const handleScroll = () => onClose();

    // `mousedown` (not click) so we close before any child button
    // absorbs the event and prevents a competing action.
    document.addEventListener('mousedown', handleClickAway, true);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickAway, true);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  return (
    <div
      ref={rootRef}
      role="menu"
      aria-label="Segment actions"
      style={{
        position: 'fixed',
        top: clampedY,
        left: clampedX,
        width: MENU_WIDTH,
        zIndex: 2000,
      }}
      className="rounded-lg border border-rgf-gray-200 bg-white shadow-lg py-1"
      // Stop native Leaflet's contextmenu handler from re-firing when
      // the user right-clicks *within* the menu (e.g. on a button).
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="px-3 py-1.5 border-b border-rgf-gray-100">
        <p className="text-[11px] uppercase tracking-wide text-rgf-gray-500">Segment</p>
        <p className="text-xs font-medium text-rgf-gray-900 truncate">#{segmentId}</p>
      </div>
      <button
        type="button"
        role="menuitem"
        disabled={busy}
        onClick={onCorrect}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-rgf-gray-800 hover:bg-rgf-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-rgf-gray-500" />
        ) : (
          <Wand2 className="h-4 w-4 text-rgf-primary-600" />
        )}
        Auto-Correct the path
      </button>
      {extendable && (
        <button
          type="button"
          role="menuitem"
          disabled={busy}
          onClick={onExtend}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-rgf-gray-800 hover:bg-rgf-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
          title="Bridge the gap between this segment and the next segment in the same check-in."
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin text-rgf-gray-500" />
          ) : (
            <ArrowRightCircle className="h-4 w-4 text-rgf-primary-600" />
          )}
          Auto-Correct + Extend to next segment
        </button>
      )}
    </div>
  );
};
